import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { getCurrentInstance, onUnmounted } from 'vue';
import { useUser } from '@/store/user';

let connection: HubConnection | undefined;

const reconnectedCallbacks: (() => void)[] = [];
const connectedCallbacks: (() => void)[] = [];
let connected = false;

// Events broadcast while the connection was down are lost, so callers typically
// reload their data here. Safe to call before connectSignalr.
export function onSignalrReconnected(callback: () => void) {
  reconnectedCallbacks.push(callback);
  return () => {
    const index = reconnectedCallbacks.indexOf(callback);
    if (index >= 0) reconnectedCallbacks.splice(index, 1);
  };
}

// Runs once the initial connection is up (immediately if it already is), for callers that
// need to pull state over the connection. Safe to call before connectSignalr.
export function onSignalrConnected(callback: () => void) {
  if (connected) {
    callback();
    return;
  }
  connectedCallbacks.push(callback);
}

export async function connectSignalr() {
  // Idempotent: if a connection has already been established (or is in the process
  // of starting/reconnecting), reuse it. Creating a second connection leaves the
  // first one alive on both client and server, so every SignalR broadcast then fires
  // any registered listeners twice.
  if (connection) return;

  const brandId = useUser().brandId;
  const url = brandId ? `/api/signalr?brandId=${encodeURIComponent(brandId)}` : '/api/signalr';

  connection = new HubConnectionBuilder()
    .withUrl(url)
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: (retryContext) => {
        // The default is [0, 2, 10, 30] seconds and then give up, but we instead keep retrying forever every 30 seconds.
        switch (retryContext.previousRetryCount) {
          case 0:
            return 0;
          case 1:
            return 2000;
          case 2:
            return 10000;
          default:
            return 30000;
        }
      },
    })
    .build();

  connection.onreconnected(() => {
    for (const callback of reconnectedCallbacks) callback();
  });

  const retryDelayInSeconds = 5;

  while (true) {
    try {
      await connection.start();
      connected = true;
      for (const callback of connectedCallbacks.splice(0)) callback();
      break; // Exit the loop if the connection is successful
    } catch (e) {
      console.error(e);
      console.log(`Retrying in ${retryDelayInSeconds} seconds...`);
      await new Promise((resolve) => setTimeout(resolve, retryDelayInSeconds * 1000));
    }
  }
}

export async function signalrInvoke<T>(methodName: string, ...args: any[]): Promise<T> {
  if (!connection) throw new Error('signalrInvoke called before connectSignalr');
  return connection.invoke<T>(methodName, ...args);
}

/** Run a callback every time the connection comes back after a drop, e.g. to re-pull
 * state that change events alone can't recover. */
export function signalrOnReconnected(callback: () => void) {
  if (!connection) throw new Error('signalrOnReconnected called before connectSignalr');
  connection.onreconnected(() => callback());
}

export function signalrListen<T extends any[]>(
  eventName: string,
  callback: (...args: T) => void,
  { listenForever } = { listenForever: false }
) {
  if (!connection) throw new Error('signalrListen called before connectSignalr');
  const conn = connection;

  conn.on(eventName, callback);

  // If inside a component and not listening forever, stop listening when the component is unmounted.
  if (listenForever) return;

  const instance = getCurrentInstance();
  if (!instance) return;

  onUnmounted(() => {
    conn.off(eventName, callback);
  });
}
